import { useCallback, useEffect, useId, useState } from 'react';
import type { Message } from 'ably';
import AblyChannelListener from '@/components/common/AblyChannelListener';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import Title from '@/components/ui/Title';
import type {
  PublicSurveySubmission,
  SurveyAnswerOption,
  SurveyResults,
  SurveyTemplate,
} from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import {
  getBackgroundStyle,
  getBorderStyle,
  getFinalAccentColor,
  getReadableTextColor,
} from '@/utils/colors';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  postAnalyticsEvent,
  submitPublicSurvey,
  submitPublicSurveyPut,
} from '@/features/live/liveThunks';
import { fetchMyPublicSurveySubmission } from '@/features/webinar/webinarThunks';
import { useChannelName } from '@/routes/AblyAuth';
import { parsePresenceUpdateLiveCount } from '@/lib/presenceUpdate';
import { isSurveyAlreadySubmittedError } from '@/lib/surveySubmissionError';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { useWebinarT } from '@/hooks/useWebinarT';

type SurveyCardProps = {
  survey: SurveyTemplate;
  visualIdentity: WorkspaceVisualIdentity;
  isAttendee?: boolean;
  isAdmin?: boolean;
  isModerator?: boolean;
  isShow?: boolean;
  showResults?: boolean;
  webinarId?: number;
  workspaceId?: number;
  sessionId?: number;
  authorization?: string;
  sessionIdentifier?: string;
  surveyResults?: SurveyResults | null;
  liveParticipantCountSync?: number;
  // Salle de test : la réponse reste locale, ni envoi ni analytics.
  onLocalSubmit?: (answer: { questionId: number; optionIds: number[] }) => void;
};

export default function SurveyCard({
  survey,
  visualIdentity,
  isAttendee = false,
  isAdmin = false,
  isModerator = false,
  isShow = false,
  showResults = false,
  webinarId,
  workspaceId,
  sessionId,
  authorization,
  sessionIdentifier,
  surveyResults: surveyResultsProp,
  liveParticipantCountSync,
  onLocalSubmit,
}: SurveyCardProps) {
  const { t } = useWebinarT('webinars');
  const dispatch = useAppDispatch();
  const uniqueId = useId();
  const publicSurveySubmission = useAppSelector(
    (s: RootState) => s.webinar.currentPublicSurveySubmission,
  );
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const question = survey?.questions?.[0];

  const hasExistingSubmission =
    publicSurveySubmission &&
    publicSurveySubmission?.templateId === survey?.id &&
    question &&
    (publicSurveySubmission?.answers?.length ?? 0) > 0;

  const isDisabled = hasSubmitted || !!hasExistingSubmission;

  useEffect(() => {
    if (!isAttendee || !question || !publicSurveySubmission) return;
    if (publicSurveySubmission?.templateId !== survey?.id) return;

    const existingAnswers = publicSurveySubmission?.answers?.filter(
      (a) => a?.questionId === question?.id,
    );
    if (existingAnswers?.length) {
      const optionIds = existingAnswers.flatMap((a) => a?.optionIds ?? []);
      setSelectedOptions(optionIds);
      setHasSubmitted(true);
    }
  }, [isAttendee, question, publicSurveySubmission, survey?.id]);

  const [surveyResultsFromAbly, setSurveyResultsFromAbly] = useState<SurveyResults | null>(null);
  const [liveParticipantCountFromChannel, setLiveParticipantCountFromChannel] = useState(0);

  const channelName = useChannelName();
  const handleChannelMessage = (message: Message) => {
    if (!channelName || !message) return;

    const normalizedMessages =
      message.name === 'batch-events' && Array.isArray(message.data)
        ? message.data.filter(
            (event): event is { name: string; data: unknown } =>
              !!event &&
              typeof event === 'object' &&
              typeof (event as { name?: unknown }).name === 'string',
          )
        : [{ name: message.name, data: message.data }];

    for (const currentMessage of normalizedMessages) {
      if (currentMessage.name === 'presence-update') {
        const live = parsePresenceUpdateLiveCount(currentMessage.data);
        if (live !== null) {
          setLiveParticipantCountFromChannel(live);
        }
        continue;
      }
      if (currentMessage.name !== 'survey-results-update') continue;
      if (!survey?.id || !workspaceId) continue;
      const data = currentMessage.data as
        | { webinarId?: number; templateId?: number; questions?: unknown[] }
        | undefined;
      if (
        data?.templateId === survey.id &&
        data?.webinarId != null &&
        Array.isArray(data?.questions)
      ) {
        setSurveyResultsFromAbly(data as SurveyResults);
      }
    }
  };
  const channelListener = channelName ? (
    <AblyChannelListener channelName={channelName} onMessage={handleChannelMessage} />
  ) : null;

  const surveyResults = isShow ? surveyResultsFromAbly : surveyResultsProp;
  const liveParticipantCount = Math.max(
    liveParticipantCountSync ?? 0,
    liveParticipantCountFromChannel,
  );

  const getOptionPercentage = useCallback(
    (optionId: number): number => {
      const canSeeResults = isAdmin || isModerator || showResults || !!surveyResults;
      if (!question || !canSeeResults || surveyResults?.templateId !== survey?.id) {
        return 0;
      }

      const questionResult = surveyResults?.questions?.find((q) => q?.questionId === question?.id);
      if (!questionResult) {
        return 0;
      }

      const optionResult = questionResult?.options?.find((opt) => opt?.optionId === optionId);
      if (!optionResult) {
        return 0;
      }

      const totalResponses =
        questionResult?.options?.reduce((sum, opt) => sum + (opt?.count ?? 0), 0) ?? 0;
      if (totalResponses === 0) {
        return 0;
      }
      return Math.round(((optionResult?.count ?? 0) / totalResponses) * 100);
    },
    [isAdmin, isModerator, showResults, surveyResults, survey?.id, question?.id],
  );

  const handleSingleChoiceChange = useCallback(
    (value: string) => {
      const selectedOption = question?.options?.find((opt) => opt.textContent === value);
      if (!selectedOption) return;
      setSelectedOptions([selectedOption.id]);
    },
    [question?.options],
  );

  const handleMultipleChoiceChange = useCallback(
    (optionId: number, checked: boolean) => {
      if (checked) {
        const newSelectedOptions = [...selectedOptions, optionId];
        if (question?.maxSelections && newSelectedOptions.length > question.maxSelections) {
          return;
        }
        setSelectedOptions(newSelectedOptions);
      } else {
        setSelectedOptions(selectedOptions.filter((id) => id !== optionId));
      }
    },
    [question?.maxSelections, selectedOptions],
  );

  const handleSubmit = useCallback(() => {
    if (onLocalSubmit) {
      if (!isAttendee || !question || selectedOptions.length === 0 || isDisabled) return;
      setHasSubmitted(true);
      onLocalSubmit({ questionId: question.id, optionIds: selectedOptions });
      return;
    }
    if (
      !isAttendee ||
      !webinarId ||
      !sessionId ||
      !authorization ||
      !question ||
      selectedOptions.length === 0 ||
      isDisabled ||
      isSubmitting
    ) {
      return;
    }

    const input = {
      sessionId,
      answers: [
        {
          questionId: question.id,
          optionIds: selectedOptions,
          freeText: null,
        },
      ],
    };

    setIsSubmitting(true);

    const send = (replace: boolean) =>
      dispatch(
        (replace ? submitPublicSurveyPut : submitPublicSurvey)({
          webinarId,
          templateId: survey?.id ?? 0,
          authorization,
          input,
        }),
      ).unwrap();

    const onSuccess = (submission: PublicSurveySubmission | null) => {
      setHasSubmitted(true);
      setIsSubmitting(false);
      // accepté par live-api : pas encore persisté, et le consumer produit SURVEY_SUBMISSION
      if (submission === null) return;
      dispatch(
        fetchMyPublicSurveySubmission({
          webinarId,
          templateId: survey?.id ?? 0,
          authorization,
        }),
      );
      if (sessionIdentifier) {
        dispatch(
          postAnalyticsEvent({
            webinarId,
            sessionIdentifier,
            authorization,
            input: {
              eventType: 'SURVEY_SUBMISSION',
              payloadJson: JSON.stringify({
                templateId: survey?.id,
                questionId: question.id,
                optionIds: selectedOptions,
                sessionId,
              }),
              occurredAt: new Date().toISOString(),
            },
          }),
        );
      }
    };

    // la carte reste ouverte : le spectateur peut renvoyer sa réponse
    const onError = () => {
      toast.error(t('live.survey.sendError'));
      setIsSubmitting(false);
    };

    send(hasSubmitted)
      .catch((error: unknown) => {
        if (!hasSubmitted && isSurveyAlreadySubmittedError(error)) return send(true);
        throw error;
      })
      .then(onSuccess, onError);
  }, [
    isAttendee,
    webinarId,
    sessionId,
    authorization,
    question,
    survey?.id,
    selectedOptions,
    hasSubmitted,
    isDisabled,
    isSubmitting,
    sessionIdentifier,
    dispatch,
    onLocalSubmit,
  ]);

  if (!question) {
    return channelListener;
  }

  const canSeeResults = isAdmin || isModerator || showResults || !!surveyResults;
  const questionResult = surveyResults?.questions?.find((q) => q?.questionId === question?.id);
  const totalResponses =
    questionResult?.options?.reduce((sum, opt) => sum + (opt?.count ?? 0), 0) ?? 0;
  const participationPercentage =
    liveParticipantCount > 0
      ? Math.min(100, Math.round((totalResponses / liveParticipantCount) * 100))
      : 0;

  // Bouton « Valider » : par défaut en couleur de contraste pour ressortir du fond de carte
  // (accent), avec un texte calculé pour rester lisible quelle que soit la couleur retenue.
  const buttonBackgroundColor = getFinalAccentColor(
    survey.accentColor || 'contrast',
    undefined,
    visualIdentity,
  );
  const buttonTextColor = getReadableTextColor(buttonBackgroundColor);

  return (
    <div
      className="max-w-[550px] w-full mx-auto rounded-lg p-4 flex flex-col gap-4"
      style={{
        ...getBackgroundStyle(survey.backgroundColor ?? 'accent', undefined, visualIdentity),
        ...getBorderStyle(survey.borderColor ?? 'accent', undefined, visualIdentity),
      }}
    >
      {channelListener}
      <Title level={2} className="text-base lg:text-lg">
        <span
          style={{
            color: survey?.titleColor ?? '#000000',
          }}
        >
          {question?.textContent ?? ''}
        </span>
      </Title>
      {question.type === 'SINGLE_CHOICE' ? (
        <>
          <RadioGroup
            value={
              selectedOptions.length > 0
                ? question.options?.find((opt) => opt.id === selectedOptions[0])?.textContent || ''
                : ''
            }
            onValueChange={handleSingleChoiceChange}
            disabled={!isAttendee || isDisabled}
          >
            {question.options?.map((option: SurveyAnswerOption) => {
              const shouldShowResults = surveyResults
                ? true
                : ((isAdmin || isModerator) && isShow) || showResults;
              const percentage = shouldShowResults ? getOptionPercentage(option.id) : 0;
              const optionId = `${uniqueId}-${survey?.id}-${option.id}`;
              return (
                <Label
                  key={option.id}
                  htmlFor={optionId}
                  className={`relative flex items-center space-x-2 p-2 rounded-lg bg-white border border-input ${
                    isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  }`}
                >
                  {shouldShowResults && (
                    <div
                      className="absolute inset-0 transition-all duration-300 ease-in-out rounded-lg pointer-events-none"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: getFinalAccentColor(
                          survey.accentColor || 'contrast',
                          undefined,
                          visualIdentity,
                        ),
                        opacity: 0.3,
                      }}
                    />
                  )}
                  <RadioGroupItem
                    value={option?.textContent ?? ''}
                    id={optionId}
                    style={
                      {
                        '--accent-color': getFinalAccentColor(
                          survey.accentColor || 'contrast',
                          undefined,
                          visualIdentity,
                        ),
                      } as React.CSSProperties
                    }
                    className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color) relative z-10"
                  />
                  <span className="text-sm text-black relative z-10">
                    {option?.textContent ?? ''}
                    {shouldShowResults && <span className="text-tertiary ml-2">{percentage}%</span>}
                  </span>
                </Label>
              );
            })}
          </RadioGroup>
          {isAttendee && ((webinarId && sessionId && authorization) || onLocalSubmit) && (
            <Button
              onClick={handleSubmit}
              disabled={selectedOptions.length === 0 || isDisabled || isSubmitting}
              className="w-full"
              style={{
                backgroundColor: buttonBackgroundColor,
                color: buttonTextColor,
              }}
            >
              {t(isSubmitting ? 'live.survey.sending' : 'live.survey.submit')}
            </Button>
          )}
          {canSeeResults && participationPercentage > 0 && (
            <span
              style={{
                color: survey?.titleColor ?? '#000000',
              }}
              className="font-normal text-sm ml-1"
            >
              {liveParticipantCount > 0 &&
                `${t('live.participationPercentage', { percentage: participationPercentage })}`}
            </span>
          )}
        </>
      ) : (
        <div className="flex flex-col space-y-2">
          {question.options?.map((option: SurveyAnswerOption) => {
            const shouldShowResults = surveyResults
              ? true
              : ((isAdmin || isModerator) && isShow) || showResults;
            const percentage = shouldShowResults ? getOptionPercentage(option?.id ?? 0) : 0;
            const optionId = `${survey?.id}-${option.id}`;
            return (
              <Label
                key={option.id}
                htmlFor={optionId}
                className={`relative flex items-center space-x-2 p-2 rounded-lg bg-white border border-input ${
                  isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {shouldShowResults && (
                  <div
                    className="absolute inset-0 transition-all duration-300 ease-in-out rounded-lg"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: getFinalAccentColor(
                        survey.accentColor || 'contrast',
                        undefined,
                        visualIdentity,
                      ),
                      opacity: 0.3,
                    }}
                  />
                )}
                <Checkbox
                  id={optionId}
                  checked={selectedOptions.includes(option?.id ?? 0)}
                  onCheckedChange={(checked) =>
                    handleMultipleChoiceChange(option?.id ?? 0, checked === true)
                  }
                  disabled={!isAttendee || isDisabled}
                  style={
                    {
                      '--accent-color': getFinalAccentColor(
                        survey.accentColor || 'contrast',
                        undefined,
                        visualIdentity,
                      ),
                    } as React.CSSProperties
                  }
                  className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color) relative z-10"
                />
                <span className="text-sm text-black relative z-10">
                  {option?.textContent ?? ''}
                  {shouldShowResults && <span className="text-tertiary ml-2">{percentage}%</span>}
                </span>
              </Label>
            );
          })}
          {canSeeResults && (
            <span
              style={{
                color: survey?.titleColor ?? '#000000',
              }}
              className="font-normal text-sm "
            >
              {liveParticipantCount > 0 &&
                ` ${participationPercentage}${t('survey.participationPercentage')}`}
            </span>
          )}
          {isAttendee && ((webinarId && sessionId && authorization) || onLocalSubmit) && (
            <Button
              onClick={handleSubmit}
              disabled={selectedOptions.length === 0 || isDisabled || isSubmitting}
              className="w-full"
              style={{
                backgroundColor: buttonBackgroundColor,
                color: buttonTextColor,
              }}
            >
              {t(isSubmitting ? 'live.survey.sending' : 'live.survey.submit')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
